import React, { useEffect, useState } from "react";
import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail } from "lucide-react";
import { COMPANY } from "../data/site";

const NAV_ITEMS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/capabilities", label: "Capabilities" },
  { to: "/government-contracting", label: "Government Contracting" },
  { to: "/industries-served", label: "Industries" },
  { to: "/past-performance", label: "Past Performance" },
  { to: "/contact", label: "Contact" },
];

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function TopBar() {
  return (
    <div className="hidden md:block bg-[#0A1F35] text-white" data-testid="top-bar">
      <div className="container-x flex items-center justify-between py-2 text-xs">
        <div className="flex items-center gap-6 text-white/80">
          <span className="text-overline">UEI · FGXGDXF8DGT7</span>
          <span className="text-overline">CAGE · 8DD93</span>
          <span className="text-overline text-[#9BB7A2]">SAM.gov Registered</span>
        </div>
        <div className="flex items-center gap-5">
          <a
            href={COMPANY.phoneHref}
            className="flex items-center gap-2 hover:text-[#C4A265] transition-colors"
            data-testid="top-bar-phone"
          >
            <Phone size={13} strokeWidth={2} />
            {COMPANY.phone}
          </a>
          <a
            href={COMPANY.emailHref}
            className="flex items-center gap-2 hover:text-[#C4A265] transition-colors"
            data-testid="top-bar-email"
          >
            <Mail size={13} strokeWidth={2} />
            {COMPANY.email}
          </a>
        </div>
      </div>
    </div>
  );
}

function Brand() {
  return (
    <Link to="/" className="flex items-center gap-3 group" data-testid="brand-link">
      <span className="relative inline-flex h-10 w-10 items-center justify-center bg-[#0A1F35] text-white font-serif text-lg leading-none">
        <span className="absolute inset-[3px] border border-white/30" aria-hidden="true" />
        SS
      </span>
      <span className="flex flex-col leading-tight">
        <span className="font-serif text-[1.05rem] text-[#0A1F35] font-bold tracking-tight">
          Street Society <span className="text-[#4B7055]">LLC</span>
        </span>
        <span className="text-overline text-[#4A5568] text-[10px]">
          Multi-Capability Service Provider
        </span>
      </span>
    </Link>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <TopBar />
      <header
        data-testid="site-header"
        className={`sticky top-0 z-50 w-full bg-white transition-shadow ${
          scrolled ? "shadow-[0_1px_0_0_#E2E8F0,0_8px_24px_-18px_rgba(10,31,53,0.25)]" : "border-b border-[#E2E8F0]"
        }`}
      >
        <div className="container-x flex items-center justify-between py-4">
          <Brand />

          <nav className="hidden lg:flex items-center gap-7" data-testid="primary-nav">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-[13px] font-medium tracking-wide transition-colors link-underline ${
                    isActive ? "text-[#0A1F35]" : "text-[#4A5568] hover:text-[#0A1F35]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center">
            <Link to="/contact" className="btn-base btn-primary" data-testid="header-cta-contact">
              Request a Conversation
            </Link>
          </div>

          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 text-[#0A1F35]"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden border-t border-[#E2E8F0] bg-white" data-testid="mobile-menu">
            <div className="container-x py-4 flex flex-col gap-1">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `py-2.5 text-sm font-medium border-b border-[#F0F2F5] last:border-0 ${
                      isActive ? "text-[#0A1F35]" : "text-[#4A5568]"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className="btn-base btn-primary mt-3 justify-center"
                data-testid="mobile-cta-contact"
              >
                Request a Conversation
              </Link>
            </div>
          </div>
        )}
      </header>
    </>
  );
}

function Footer() {
  return (
    <footer className="bg-[#0A1F35] text-white relative overflow-hidden" data-testid="site-footer">
      <div className="container-x py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-5">
            <span className="relative inline-flex h-10 w-10 items-center justify-center bg-white text-[#0A1F35] font-serif text-lg leading-none">
              <span className="absolute inset-[3px] border border-[#0A1F35]/30" aria-hidden="true" />
              SS
            </span>
            <span className="font-serif text-xl font-bold">
              Street Society <span className="text-[#9BB7A2]">LLC</span>
            </span>
          </div>
          <p className="text-white/70 text-sm max-w-md leading-relaxed">
            Reliable multi-capability services for government, commercial, and institutional clients —
            delivered with operational discipline, compliance awareness, and dependable execution.
          </p>

          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-xs text-white/60">
            <span className="text-overline text-[#9BB7A2]">UEI · FGXGDXF8DGT7</span>
            <span className="text-overline text-[#9BB7A2]">CAGE · 8DD93</span>
            <span className="text-overline text-[#9BB7A2]">SAM.gov Registered</span>
          </div>
        </div>

        <div className="lg:col-span-3">
          <h4 className="text-overline text-white/60 mb-4">Explore</h4>
          <ul className="space-y-2.5 text-sm">
            {NAV_ITEMS.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className="text-white/85 hover:text-[#C4A265] transition-colors"
                  data-testid={`footer-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-4">
          <h4 className="text-overline text-white/60 mb-4">Contact</h4>
          <ul className="space-y-2.5 text-sm">
            <li className="text-white/85">{COMPANY.name}</li>
            <li className="text-white/85">{COMPANY.location}</li>
            <li>
              <a
                href={COMPANY.phoneHref}
                className="text-white/85 hover:text-[#C4A265] transition-colors"
                data-testid="footer-phone"
              >
                {COMPANY.phone}
              </a>
            </li>
            <li>
              <a
                href={COMPANY.emailHref}
                className="text-white/85 hover:text-[#C4A265] transition-colors"
                data-testid="footer-email"
              >
                {COMPANY.email}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-x py-5 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between text-xs text-white/55">
          <p>
            Street Society LLC &nbsp;|&nbsp; UEI: FGXGDXF8DGT7 &nbsp;|&nbsp; CAGE: 8DD93 &nbsp;|&nbsp;
            <a href={COMPANY.emailHref} className="hover:text-[#C4A265]"> hello@streetsocietyllc.com</a>
          </p>
          <p>&copy; {new Date().getFullYear()} Street Society LLC. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}

export default function Layout() {
  return (
    <div className="min-h-screen flex flex-col bg-white text-[#1A2530]">
      <ScrollToTop />
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
